import axios from 'axios';
import { BASE_URL } from '../Constants/Api';

const API_URL = BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Authorization token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Get all recruitment requirements with pagination and filtering
export const getRecruitments = async ({ stsCd, search, page = 0, size = 10, sortBy = "id", order = "desc" }) => {
  try {
    const params = new URLSearchParams();
    if (stsCd && stsCd !== "all") params.append("stsCd", stsCd);
    if (search && search.trim() !== "") params.append("search", search);
    params.append("page", page);
    params.append("size", size);
    params.append("sortBy", sortBy);
    params.append("order", order);

    const response = await api.get(`/marketing/recruitments?${params.toString()}`);
    return {
      content: response.data.content || [],
      totalElements: response.data.totalElements || 0,
      totalPages: response.data.totalPages || 0,
    };
  } catch (error) {
    console.error('Failed to fetch recruitments:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch recruitments');
  }
};

// Get a recruitment requirement by ID
export const getRecruitmentById = async (id) => {
  try {
    const response = await api.get(`/marketing/recruitments/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch recruitment by ID:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch recruitment');
  }
};

// Create a new recruitment requirement
export const createRecruitment = async (recruitmentData) => {
  try {
    const response = await api.post('/marketing/recruitments', recruitmentData);
    return response.data;
  } catch (error) {
    console.error('Failed to create recruitment:', error);
    throw new Error(error.response?.data?.message || 'Failed to create recruitment');
  }
};

// Update a recruitment requirement
export const updateRecruitment = async (id, recruitmentData) => {
  try {
    const response = await api.put(`/marketing/recruitments/${id}`, recruitmentData);
    return response.data;
  } catch (error) {
    console.error('Failed to update recruitment:', error);
    throw new Error(error.response?.data?.message || 'Failed to update recruitment');
  }
};

// Delete a recruitment requirement
export const deleteRecruitment = async (id) => {
  try {
    await api.delete(`/marketing/recruitments/${id}`);
    return true;
  } catch (error) {
    console.error('Failed to delete recruitment:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete recruitment');
  }
};

// Get all submissions for a recruitment requirement
export const getRecruitmentSubmissions = async (id, { page = 0, size = 10, sortBy = "id", order = "desc" }) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", size);
    params.append("sortBy", sortBy);
    params.append("order", order);

    const response = await api.get(`/marketing/recruitments/${id}/submissions?${params.toString()}`);
    return {
      content: response.data.content || [],
      totalElements: response.data.totalElements || 0,
      totalPages: response.data.totalPages || 0,
    };
  } catch (error) {
    console.error('Failed to fetch recruitment submissions:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch submissions');
  }
};

// Create a new submission for a recruitment requirement
export const createRecruitmentSubmission = async (id, payload) => {
  try {
    const response = await api.post(`/marketing/recruitments/${id}/submissions`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create submission:', error);
    throw new Error(error.response?.data?.message || 'Failed to create submission');
  }
};



// Update submission status to "d" (soft delete) for a recruitment requirement
export const deleteRecruitmentSubmission = async (id, submissionId) => {
  try {
    await api.put(`/marketing/recruitments/${id}/submissions/${submissionId}/status`);
    return true;
  } catch (error) {
    console.error('Failed to update submission status:', error);
    throw new Error(error.response?.data?.message || 'Failed to update submission status');
  }
};

export const getRecruitmentSubmissionById = async (id, submissionId) => {
  try {
    const response = await api.get(`/marketing/recruitments/${id}/submissions/${submissionId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch submission details:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch submission details');
  }
};

export const updateRecruitmentSubmission = async (id, payload) => {
  try {
    const response = await api.put(`/marketing/recruitments/${id}/interview-scheduled`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to update submission:', error);
    throw new Error(error.response?.data?.message || 'Failed to update submission');
  }
};

// Get all candidates
export const getCandidates = async () => {
  try {
    const response = await api.get('/marketing/recruitments/candidates');
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch candidates:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch candidates');
  }
};

export const getCandidateResumes = async (candidateId) => {
  if (!candidateId) return [];
  try {
    const response = await api.get(`/marketing/recruitments/candidates/${candidateId}/documents`);
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch candidate resumes:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch candidate resumes');
  }
};

export const getAllCandidates = async ({ page = 0, size = 10, sortBy = "id", order = "desc", search = "", stsCd = null }) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", size);
    params.append("sortBy", sortBy);
    params.append("order", order);
    if (search && search.trim() !== "") {
      params.append("search", search.trim());
    }
    if (stsCd && stsCd !== "all" && stsCd.trim() !== "") {
      params.append("stsCd", stsCd.trim());
    }
    const response = await api.get(`/marketing/recruitments/candidates/all?${params.toString()}`);
    return {
      content: response.data.content || [],
      totalElements: response.data.totalElements || 0,
      totalPages: response.data.totalPages || 0,
    };
  } catch (error) {
    console.error('Failed to fetch candidates:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch candidates');
  }
};

export const createCandidate = async (payload) => {
  try {
    const response = await api.post('/marketing/recruitments/candidates', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create candidate:', error);
    throw new Error(error.response?.data?.message || 'Failed to create candidate');
  }
};
  export const updateCandidate = async (id, payload) => {
    try {
      const response = await api.put(`/marketing/recruitments/candidates/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to update candidate:', error);
      throw new Error(error.response?.data?.message || 'Failed to update candidate');
    }
  }
  export const deleteCandidate = async (id) => {
    try {
      await api.delete(`/marketing/recruitments/candidates/${id}`);
      return true;
    } catch (error) {
      console.error('Failed to delete candidate:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete candidate');
    }
  }   
  export const getCandidateById = async (id) => {
    try {
      const response = await api.get(`/marketing/recruitments/candidates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch candidate by ID:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch candidate');
    }
  };

export const getSubmissionInfo = async (id, submissionId) => {
  try {
    const response = await api.get(`/marketing/recruitments/${id}/submissions/${submissionId}/info`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch submission info:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch submission info');
  }
};
export const updateCandidateStatus = async (id) => {
  try {
    await api.put(`/marketing/recruitments/candidates/${id}/status`);
    return true;
  } catch (error) {
    console.error('Failed to update candidate status:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update candidate status');
  }
  
};

export const getDashboardData = async () => {
  try {
    const response = await api.get('/marketing/recruitments/candidates/dashboard');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch dashboard data');
  }
};